import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import { CognitoUser } from '@/api/adminClient';
import { useAuth } from '@/hooks/useAuth';
import CreateUserDialog from './CreateUserDialog';
import BulkCreateUserDialog from './BulkCreateUserDialog';

interface UserListTableProps {
  users: CognitoUser[];
  isLoading: boolean;
  onDisableUser: (username: string) => Promise<void>;
  onEnableUser: (username: string) => Promise<void>;
  onCreateUser: (email: string) => Promise<boolean>;
  onRefresh?: () => void;
}

/**
 * 全ユーザー一覧テーブル
 * ユーザー追加・無効化操作を提供する
 */
const UserListTable: React.FC<UserListTableProps> = ({
  users,
  isLoading,
  onDisableUser,
  onEnableUser,
  onCreateUser,
  onRefresh,
}) => {
  const { userInfo } = useAuth();
  const [searchText, setSearchText] = useState('');
  const [confirmTarget, setConfirmTarget] = useState<{user: CognitoUser, action: 'enable' | 'disable'} | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(searchText.toLowerCase()) ||
      user.username.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleDisableClick = (user: CognitoUser) => {
    setConfirmTarget({ user, action: 'disable' });
  };

  const handleEnableClick = (user: CognitoUser) => {
    setConfirmTarget({ user, action: 'enable' });
  };

  const handleConfirmAction = async () => {
    if (!confirmTarget) return;
    if (confirmTarget.action === 'disable') {
      await onDisableUser(confirmTarget.user.username);
    } else {
      await onEnableUser(confirmTarget.user.username);
    }
    setConfirmTarget(null);
  };

  const getStatusChip = (user: CognitoUser) => {
    if (!user.enabled) {
      return <Chip label="無効" size="small" color="error" variant="outlined" />;
    }
    if (user.status === 'FORCE_CHANGE_PASSWORD') {
      return <Chip label="初回ログイン待ち" size="small" color="warning" variant="outlined" />;
    }
    return <Chip label="有効" size="small" color="success" variant="outlined" />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          disabled={isLoading}
        >
          ユーザー追加
        </Button>
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => setBulkDialogOpen(true)}
          disabled={isLoading}
        >
          CSV一括登録
        </Button>
        <TextField
          size="small"
          placeholder="メール・ユーザー名で検索"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 260 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredUsers.length} 件
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>メールアドレス</TableCell>
              <TableCell>ユーザー名</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.username} hover>
                <TableCell>{user.email}</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                  {user.username}
                </TableCell>
                <TableCell>{getStatusChip(user)}</TableCell>
                <TableCell align="right">
                  <Tooltip title={user.email === userInfo?.email ? '自分自身の状態は変更できません' : user.enabled ? 'ユーザーを無効化' : 'ユーザーを有効化'}>
                    <span>
                      {user.enabled ? (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDisableClick(user)}
                          disabled={isLoading || user.email === userInfo?.email}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleEnableClick(user)}
                          disabled={isLoading || user.email === userInfo?.email}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      )}
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {searchText ? '検索結果がありません' : 'ユーザーが存在しません'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ユーザー追加ダイアログ */}
      <CreateUserDialog
        open={createDialogOpen}
        isLoading={isLoading}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={async (email) => {
          const success = await onCreateUser(email);
          if (success) setCreateDialogOpen(false);
          return success;
        }}
      />

      {/* CSV一括登録ダイアログ */}
      <BulkCreateUserDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        onSuccess={() => {
          onRefresh?.();
        }}
        existingUsers={users}
        createUserFn={onCreateUser}
      />

      {/* 状態変更確認ダイアログ */}
      <Dialog open={!!confirmTarget} onClose={() => setConfirmTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmTarget?.action === 'disable' ? 'ユーザーの無効化' : 'ユーザーの有効化'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            以下のユーザーを{confirmTarget?.action === 'disable' ? '無効化' : '有効化'}しますか？
            <br />
            <strong>{confirmTarget?.user.email}</strong>
            <br />
            {confirmTarget?.action === 'disable' 
              ? '無効化するとログインできなくなります。' 
              : '有効化すると再びログインできるようになります。'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTarget(null)}>キャンセル</Button>
          <Button
            variant="contained"
            color={confirmTarget?.action === 'disable' ? 'error' : 'success'}
            onClick={handleConfirmAction}
            disabled={isLoading}
          >
            {confirmTarget?.action === 'disable' ? '無効化する' : '有効化する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserListTable;
